import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import usePageMeta from '../hooks/usePageMeta';
import './TermsPage.css';

// PLACEHOLDERS — replace with real values before launch:
const RESPONSIBLE_NAME    = 'Humberto Solano Martínez';
const RESPONSIBLE_RFC     = 'SOMH931117LZ0';
const RESPONSIBLE_ADDRESS = '6 de Enero 125, Granjas Navidad, Cuajimalpa de Morelos, CDMX';
const PRIVACY_EMAIL       = 'hey@bluppimart.com';

const TermsPage = () => {
  const { t } = useTranslation('terms');

  usePageMeta({
    title: t('metaTitle'),
    description: t('metaDescription'),
  });

  // The legal body below stays in Spanish because the document is the binding
  // version under Mexican law (LFPDPPP / LFPC). The EN catalog exposes a
  // header notice + the page chrome (title, footer) so EN users can navigate.
  const noticeBody = t('spanishOnlyNotice', { email: PRIVACY_EMAIL });

  return (
    <div className="terms-shell">
      <article className="terms-page">
        <header className="terms-header">
          <h1 className="terms-title">{t('title')}</h1>
          {noticeBody && (
            <p className="terms-language-notice" style={{ background: '#fef9e7', border: '1px solid #f0d066', padding: '0.75rem 1rem', borderRadius: '8px', margin: '0.75rem 0 1.25rem', fontSize: '0.9rem', color: '#7a5a00' }}>
              <Trans
                i18nKey="terms:spanishOnlyNotice"
                values={{ email: PRIVACY_EMAIL }}
                components={{ emailLink: <a href={`mailto:${PRIVACY_EMAIL}`} aria-label={PRIVACY_EMAIL} /> }}
              />
            </p>
          )}
          <nav className="terms-toc" aria-label="Tabla de contenidos">
            <a href="#aviso-de-privacidad">I. Aviso de Privacidad</a>
            <a href="#terminos-de-uso">II. Términos de Uso</a>
          </nav>
        </header>

        <section id="aviso-de-privacidad" className="terms-section">
          <h2 className="terms-h2">I. Aviso de Privacidad</h2>
          <p className="terms-lead">
            El presente Aviso se emite en cumplimiento de la{' '}
            <strong>Ley Federal de Protección de Datos Personales en Posesión
            de los Particulares (LFPDPPP)</strong>, su Reglamento y los
            Lineamientos del Aviso de Privacidad aplicables.
          </p>

          <h3 className="terms-h3">1. Identidad y domicilio del responsable</h3>
          <p>
            {RESPONSIBLE_NAME} (en adelante <strong>“Bluppimart”</strong>),
            persona física con actividad empresarial, con Registro Federal de
            Contribuyentes {RESPONSIBLE_RFC} y domicilio en{' '}
            {RESPONSIBLE_ADDRESS}, es el responsable del tratamiento de sus
            datos personales.
          </p>
          <p>
            Para cualquier asunto relacionado con la protección de sus datos
            personales puede contactarnos al correo{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
          </p>

          <h3 className="terms-h3">2. Datos personales que recabamos</h3>
          <p>
            Al crear una cuenta, navegar la plataforma o enviar una solicitud
            de perfil de artista podemos recabar los siguientes datos
            personales:
          </p>
          <ul>
            <li><strong>De identificación:</strong> nombre completo, alias artístico, correo electrónico y número de WhatsApp.</li>
            <li><strong>De contacto y entrega:</strong> domicilio postal al realizar una compra.</li>
            <li><strong>De perfil de artista:</strong> biografía, manifiesto y archivos (imágenes, video) que envías de forma voluntaria.</li>
            <li><strong>Patrimoniales o financieros (solo artistas que venden en la plataforma):</strong> nombre del banco, titular de la cuenta y CLABE interbancaria, y los links de pago de Mercado Pago o Clip de sus piezas. Estos datos se recaban con su consentimiento expreso.</li>
            <li><strong>De transacción:</strong> detalles de tus pedidos. El procesamiento de tarjetas se realiza directamente por Stripe; Bluppimart <strong>no almacena</strong> números completos de tarjeta de crédito o débito.</li>
            <li><strong>De navegación:</strong> cookies de sesión y datos técnicos básicos (dirección IP, navegador, idioma).</li>
            <li><strong>Datos sensibles:</strong> Bluppimart no recaba datos personales sensibles. Los datos bancarios de los artistas son datos financieros, no sensibles, y se tratan con su consentimiento expreso conforme al Artículo 8 de la LFPDPPP.</li>
          </ul>

          <h3 className="terms-h3">3. Finalidades primarias del tratamiento</h3>
          <p>Sus datos personales son tratados para las siguientes finalidades necesarias:</p>
          <ul>
            <li>Crear y administrar su cuenta de usuario.</li>
            <li>Procesar pedidos, cobros, envíos, cambios, devoluciones y atención a clientes.</li>
            <li>Recibir, revisar y resolver solicitudes para aparecer como artista en la plataforma.</li>
            <li>Permitir que los compradores paguen directamente al artista por transferencia o con su link de pago y, en su caso, pagarle al artista sus ventas cobradas con tarjeta.</li>
            <li>Contactarle por correo electrónico y/o WhatsApp para asuntos operativos relacionados con su cuenta, pedidos o solicitud.</li>
            <li>Cumplir con obligaciones fiscales, contables y legales aplicables.</li>
          </ul>

          <h3 className="terms-h3">4. Finalidades secundarias (opcionales)</h3>
          <p>
            Con su consentimiento expreso, sus datos también podrán ser
            tratados para:
          </p>
          <ul>
            <li>Enviar boletines, novedades sobre artistas y promociones de la plataforma.</li>
            <li>Realizar análisis estadísticos y mejoras de experiencia.</li>
          </ul>
          <p>
            <strong>Estas finalidades requieren su consentimiento explícito</strong>,
            manifestado al momento del registro o al aceptar expresamente las
            comunicaciones promocionales (casilla de suscripción al registrarse,
            al comprar o en la página principal). Si en cualquier momento desea
            retirar su consentimiento, use el enlace para darse de baja incluido
            en cada correo, la sección de correos de su cuenta, o escríbanos a{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>. La
            negativa a estas finalidades <em>no</em> condiciona la prestación
            del servicio.
          </p>

          <h3 className="terms-h3">5. Transferencias de datos a terceros</h3>
          <p>
            Cuando un comprador elige pagar una pieza por transferencia, le
            mostramos el banco, el titular y la CLABE del artista de esa pieza,
            únicamente para que realice ese pago (Artículo 37, fracción VII de
            la LFPDPPP). Los pagos con link se hacen en Mercado Pago o Clip, que
            tratan los datos del comprador conforme a sus propios avisos de
            privacidad; Bluppimart no recibe datos de tarjeta en esos pagos.
          </p>
          <p>
            Para la operación de la plataforma compartimos algunos datos con
            proveedores tecnológicos que actúan como encargados conforme al
            Artículo 50 del Reglamento de la LFPDPPP. Los principales,
            ubicados fuera de México, son:
          </p>
          <ul>
            <li>
              <strong>Supabase, Inc.</strong> (Estados Unidos de América) —
              hospedaje de base de datos y almacenamiento de archivos.
            </li>
            <li>
              <strong>Stripe, Inc.</strong> (Estados Unidos de América) —
              procesamiento de pagos con tarjeta.
            </li>
            <li>
              <strong>Mailgun Technologies, Inc. (Sinch)</strong> (Estados
              Unidos de América) — envío de correos electrónicos
              transaccionales y, con su consentimiento, promocionales.
            </li>
          </ul>
          <p>
            Estas transferencias son necesarias para mantener la relación
            jurídica entre usted y Bluppimart y operan al amparo del Artículo
            37, fracciones IV y VI de la LFPDPPP, por lo que no requieren su
            consentimiento adicional. Los proveedores se han comprometido
            contractualmente a observar un nivel de protección equivalente al
            descrito en este Aviso.
          </p>
          <p>
            Bluppimart <strong>no vende, alquila ni comercializa</strong> sus
            datos personales con terceros.
          </p>

          <h3 className="terms-h3">6. Derechos ARCO y revocación del consentimiento</h3>
          <p>
            Usted tiene derecho a <strong>Acceder</strong> a sus datos
            personales que tenemos en nuestro poder, conocer el uso que les
            damos, <strong>Rectificarlos</strong> si son inexactos,{' '}
            <strong>Cancelarlos</strong> cuando considere que no son necesarios
            para alguna de las finalidades señaladas, así como{' '}
            <strong>Oponerse</strong> a su tratamiento para fines específicos
            (derechos ARCO). También puede revocar el consentimiento que nos
            haya otorgado.
          </p>
          <p>
            Para ejercer cualquiera de estos derechos, envíe una solicitud al
            correo <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>{' '}
            con la siguiente información:
          </p>
          <ul>
            <li>Su nombre completo y un medio para contactarle (correo electrónico).</li>
            <li>Copia de identificación oficial vigente que acredite su identidad o, en su caso, la de su representante legal.</li>
            <li>Descripción clara y precisa del derecho que desea ejercer y de los datos personales involucrados.</li>
          </ul>
          <p>
            Le responderemos en un plazo máximo de <strong>20 días hábiles</strong>{' '}
            contados a partir de la recepción de su solicitud, conforme al
            Artículo 32 de la LFPDPPP. Si su petición resulta procedente, se
            hará efectiva dentro de los 15 días hábiles siguientes a la
            respuesta. Si considera que su derecho fue vulnerado, puede acudir
            ante la autoridad competente en materia de protección de datos
            personales, actualmente la <strong>Secretaría Anticorrupción y
            Buen Gobierno</strong>.
          </p>

          <h3 className="terms-h3">7. Cookies y tecnologías similares</h3>
          <p>
            Utilizamos cookies de sesión técnicamente necesarias para mantener
            su inicio de sesión y el contenido de su bolsa. Estas
            cookies no requieren consentimiento adicional por ser
            indispensables para el servicio. <strong>No utilizamos cookies
            publicitarias ni de seguimiento de terceros</strong> en este
            momento.
          </p>

          <h3 className="terms-h3">8. Modificaciones al Aviso de Privacidad</h3>
          <p>
            Cualquier modificación a este Aviso será publicada en esta misma
            página con una nueva fecha de actualización. Cuando los cambios
            afecten finalidades primarias o impliquen nuevas transferencias se
            le notificará por correo electrónico al menos con 30 días de
            anticipación.
          </p>
        </section>

        <section id="terminos-de-uso" className="terms-section">
          <h2 className="terms-h2">II. Términos y Condiciones de Uso</h2>
          <p className="terms-lead">
            Los presentes Términos se rigen, entre otras disposiciones, por el
            Código de Comercio (Artículos 89 a 114) y la{' '}
            <strong>Ley Federal de Protección al Consumidor</strong> (en
            particular el Artículo 76 BIS).
          </p>

          <h3 className="terms-h3">1. Aceptación</h3>
          <p>
            El uso de la plataforma Bluppimart implica la aceptación expresa
            e incondicional de estos Términos y del Aviso de Privacidad arriba
            descrito. Si no está de acuerdo con cualquiera de las
            disposiciones, debe abstenerse de utilizar la plataforma.
          </p>

          <h3 className="terms-h3">2. Descripción del servicio</h3>
          <p>
            Bluppimart es una plataforma de comercio electrónico dedicada a la
            venta de figuras coleccionables y piezas de arte conocidas como{' '}
            <em>Art Toys</em>. Operamos en{' '}
            <strong>pesos mexicanos (MXN)</strong> y enviamos dentro del
            territorio nacional. Las descripciones, imágenes, precios y
            disponibilidad pueden cambiar sin previo aviso.
          </p>

          <h3 className="terms-h3">3. Cuenta de usuario</h3>
          <p>Para realizar compras o solicitar un perfil de artista debe crear una cuenta. Al hacerlo, usted manifiesta que:</p>
          <ul>
            <li>Es mayor de edad (18 años cumplidos);</li>
            <li>La información proporcionada es verídica, completa y actualizada;</li>
            <li>Mantendrá la confidencialidad de su contraseña y de las credenciales asociadas a su cuenta;</li>
            <li>Notificará a Bluppimart de inmediato ante cualquier sospecha de acceso no autorizado.</li>
          </ul>

          <h3 className="terms-h3">4. Compras y pagos</h3>
          <p>
            Las compras se rigen por el Artículo 76 BIS de la Ley Federal de
            Protección al Consumidor. Al confirmar una orden:
          </p>
          <ul>
            <li>Recibirá un comprobante electrónico con el detalle de productos, precios, impuestos y método de envío.</li>
            <li>Acepta las políticas de envío, cambios y devoluciones publicadas en la plataforma.</li>
            <li>El cargo se efectúa al momento de confirmar el pago a través de Stripe; Bluppimart no tiene acceso al número completo de su tarjeta.</li>
            <li><strong>Pre-órdenes:</strong> los productos en pre-orden se cobran al 50% como anticipo, y el saldo restante al confirmarse la disponibilidad de la pieza.</li>
            <li>Los envíos están sujetos a la cobertura de los servicios de mensajería contratados. La tarifa estándar de envío es de $200 MXN, salvo que el producto se entregue en un evento presencial.</li>
          </ul>

          <h3 className="terms-h3">5. Solicitud de perfil de artista</h3>
          <p>
            La sección de <Link to="/artists">Artistas</Link> de la plataforma
            se nutre de solicitudes voluntarias enviadas a través del{' '}
            <Link to="/account/apply-artist">formulario público de solicitud</Link>.
            Al enviar una solicitud, usted:
          </p>
          <ul>
            <li>Autoriza al equipo de Bluppimart a revisar la información, imágenes, video y demás materiales que envíe.</li>
            <li><strong>Conserva en todo momento los derechos de autor</strong> y la propiedad sobre su obra.</li>
            <li>Otorga a Bluppimart una <strong>licencia no exclusiva, gratuita y limitada</strong> para reproducir, exhibir y comunicar públicamente los materiales enviados, únicamente con fines editoriales y promocionales dentro de la plataforma y sus canales oficiales de comunicación (incluyendo redes sociales).</li>
            <li>Acepta que su perfil <strong>no se publica</strong> hasta ser aprobado por el equipo. La aprobación es discrecional y no genera obligación contractual alguna.</li>
            <li>Podrá solicitar la baja de su perfil o de los materiales enviados en cualquier momento escribiendo a <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.</li>
          </ul>

          <h3 className="terms-h3">6. Propiedad intelectual</h3>
          <p>
            La marca <strong>Bluppimart</strong>, el logotipo, el diseño del
            sitio, los textos editoriales y el código fuente son propiedad del
            responsable o se encuentran licenciados a su favor. Está prohibida
            su reproducción, copia, distribución o explotación comercial sin
            autorización previa y por escrito.
          </p>
          <p>
            Los productos vendidos en la plataforma son propiedad de sus
            respectivos artistas, marcas o titulares de derechos. Cada uno
            conserva su titularidad sobre los derechos de autor, marcas y
            demás derechos de propiedad industrial.
          </p>

          <h3 className="terms-h3">7. Conducta del usuario</h3>
          <p>Está prohibido al usuario:</p>
          <ul>
            <li>Subir contenido que infrinja derechos de terceros, sea ilícito, falso, ofensivo o discriminatorio.</li>
            <li>Suplantar la identidad de otra persona o entidad.</li>
            <li>Realizar pagos fraudulentos, manipular precios o utilizar las promociones de manera indebida.</li>
            <li>Interferir con el funcionamiento técnico de la plataforma, intentar acceder a áreas restringidas, o utilizar bots, scrapers o medios automatizados no autorizados.</li>
          </ul>
          <p>
            El incumplimiento de cualquiera de estas disposiciones puede
            resultar en la suspensión o cancelación inmediata de la cuenta,
            sin perjuicio de las acciones legales que correspondan.
          </p>

          <h3 className="terms-h3">8. Limitación de responsabilidad</h3>
          <p>
            Bluppimart no se hace responsable por interrupciones del servicio
            derivadas de causas de fuerza mayor, caso fortuito, fallas técnicas
            de proveedores (hospedaje, pagos, mensajería) o el uso indebido por
            parte del usuario. En todo caso, la responsabilidad máxima de
            Bluppimart se limita al monto efectivamente pagado por el usuario
            en la transacción correspondiente.
          </p>

          <h3 className="terms-h3">9. Modificaciones</h3>
          <p>
            Bluppimart puede modificar estos Términos en cualquier momento. La
            versión vigente será siempre la publicada en esta página, con su
            fecha de última actualización al final del documento. El uso continuado de la
            plataforma con posterioridad a la publicación implica la
            aceptación de las modificaciones.
          </p>

          <h3 className="terms-h3">10. Legislación aplicable y jurisdicción</h3>
          <p>
            Estos Términos se rigen por las leyes de los Estados Unidos
            Mexicanos. Para la interpretación, cumplimiento o ejecución de
            cualquier controversia derivada del presente documento, las partes
            se someten expresamente a la jurisdicción de los{' '}
            <strong>tribunales competentes de la Ciudad de México</strong>,
            renunciando a cualquier otro fuero que pudiera corresponderles por
            razón de su domicilio presente o futuro.
          </p>

          <h3 className="terms-h3">11. Contacto</h3>
          <p>
            Para cualquier duda relacionada con estos Términos o con el Aviso
            de Privacidad puede escribir a{' '}
            <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>.
          </p>
        </section>

        <footer className="terms-footer">
          <p>{t('lastUpdatedLabel', { date: t('lastUpdated') })}</p>
          <p>
            <Link to="/account/apply-artist">{t('backToApply')}</Link>
          </p>
        </footer>
      </article>
    </div>
  );
};

export default TermsPage;
